/**
 * BalanceDetailPanel — Floating panel with sliders for editing a single unit's stats.
 *
 * For enemies: per-type HP/Speed/Damage/Range multipliers (written to ENEMY_TYPE_STAT_OVERRIDES).
 * For robots: read-only stat display (robot stats are emergent from components).
 *
 * Rendered as a Phaser Container on top of the BalanceChartScene.
 */

import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT, ENEMY_STATS, EnemyType } from '@/config/GameConfig';
import { fillTechRect, drawTechRect, createTechButton, TECH } from '@/ui/TechUI';
import { SoundManager } from '@/systems/SoundManager';
import {
  getEnemyTypeStatOverride,
  setEnemyTypeStatOverride,
  saveEnemyTypeStatOverrides,
  EnemyStatOverride,
} from '@/config/GodMode';
import { BalanceUnit, BalanceAxis, AXIS_LABELS } from '@/config/BalanceStats';
import { EventBus, GameEvents } from '@/events/EventBus';

const PANEL_W = 280;
const PANEL_H = 300;
const PAD = 12;
const TRACK_W = PANEL_W - PAD * 2 - 50;

interface SliderDef {
  key: keyof EnemyStatOverride;
  label: string;
  min: number;
  max: number;
  step: number;
}

const ENEMY_SLIDERS: SliderDef[] = [
  { key: 'hpMult', label: 'HP Mult', min: 0.1, max: 5, step: 0.1 },
  { key: 'speedMult', label: 'Speed Mult', min: 0.1, max: 3, step: 0.1 },
  { key: 'damageMult', label: 'Damage Mult', min: 0.1, max: 5, step: 0.1 },
  { key: 'rangeMult', label: 'Range Mult', min: 0.1, max: 3, step: 0.1 },
];

export function showDetailPanel(
  scene: Phaser.Scene,
  unit: BalanceUnit,
  onClose: () => void,
): Phaser.GameObjects.Container {
  const px = Math.min(CANVAS_WIDTH - PANEL_W - 10, Math.max(10, CANVAS_WIDTH / 2 - PANEL_W / 2));
  const py = Math.min(CANVAS_HEIGHT - PANEL_H - 10, Math.max(60, CANVAS_HEIGHT / 2 - PANEL_H / 2));

  const container = scene.add.container(px, py).setDepth(300);

  // Background
  const bg = scene.add.graphics();
  fillTechRect(bg, 0, 0, PANEL_W, PANEL_H, 0x0d0d1a, 0.97);
  drawTechRect(bg, 0, 0, PANEL_W, PANEL_H, unit.isEnemy ? 0xff4444 : 0x4488ff, 0.6);
  container.add(bg);

  // Title
  const titleColor = unit.isEnemy ? '#ff8888' : '#88aaff';
  const titleText = scene.add.text(PAD, PAD, unit.name, {
    fontSize: '14px', fontFamily: 'monospace', fontStyle: 'bold', color: titleColor,
  });
  container.add(titleText);

  const typeTag = unit.isEnemy ? (unit.isFlying ? 'ENEMY ✈' : 'ENEMY') : (unit.isFlying ? 'ROBOT ✈' : 'ROBOT');
  const tag = scene.add.text(PANEL_W - PAD, PAD + 2, typeTag, {
    fontSize: '9px', fontFamily: 'monospace', color: '#666688',
  }).setOrigin(1, 0);
  container.add(tag);

  // Stats summary
  let cy = PAD + 26;
  const statLine = (label: string, val: string) => {
    const t = scene.add.text(PAD, cy, `${label}: ${val}`, {
      fontSize: '9px', fontFamily: 'monospace', color: '#aaaacc',
    });
    container.add(t);
    cy += 14;
  };

  for (const axis of Object.values(BalanceAxis)) {
    const v = unit.stats[axis];
    const formatted = v >= 100 ? Math.round(v).toString() : v.toFixed(2);
    statLine(AXIS_LABELS[axis], formatted);
  }

  cy += 8;

  // Sliders (enemies only)
  if (unit.isEnemy && unit.enemyType) {
    const etype = unit.enemyType;
    const currentOv = getEnemyTypeStatOverride(etype);

    const divider = scene.add.graphics();
    divider.lineStyle(1, TECH.COLOR, 0.2);
    divider.lineBetween(PAD, cy, PANEL_W - PAD, cy);
    container.add(divider);
    cy += 6;

    const sliderLabel = scene.add.text(PAD, cy, 'PER-TYPE OVERRIDES', {
      fontSize: '9px', fontFamily: 'monospace', fontStyle: 'bold', color: '#ff8844',
    });
    container.add(sliderLabel);
    cy += 16;

    for (const sd of ENEMY_SLIDERS) {
      cy = addSlider(scene, container, PAD, cy, sd, currentOv[sd.key], (newVal) => {
        const ov = getEnemyTypeStatOverride(etype);
        setEnemyTypeStatOverride(etype, { ...ov, [sd.key]: newVal });
      });
    }

    // Reset button
    const resetBtn = createTechButton(scene, PAD + 40, cy + 4, 'RESET', {
      onClick: () => {
        setEnemyTypeStatOverride(etype, { hpMult: 1, speedMult: 1, damageMult: 1, rangeMult: 1 });
        saveEnemyTypeStatOverrides();
        SoundManager.play('click');
        container.destroy();
        onClose();
      },
      width: 60, height: 20, fontSize: '9px', color: TECH.DANGER, depth: 301,
    });
    container.add(resetBtn);
  } else {
    // Robot: read-only note
    const note = scene.add.text(PAD, cy, 'Robot stats are emergent from\ncomponents. Edit in Workshop.', {
      fontSize: '9px', fontFamily: 'monospace', color: '#666688', lineSpacing: 2,
    });
    container.add(note);
  }

  // Close button (X)
  const closeBtn = createTechButton(scene, PANEL_W - 16, 8, 'X', {
    onClick: () => {
      saveEnemyTypeStatOverrides();
      SoundManager.play('click');
      container.destroy();
      onClose();
    },
    width: 20, height: 20, fontSize: '10px', color: TECH.DANGER, depth: 301,
  });
  container.add(closeBtn);

  // Click-away to close
  const blocker = scene.add.zone(CANVAS_WIDTH / 2 - px, CANVAS_HEIGHT / 2 - py, CANVAS_WIDTH, CANVAS_HEIGHT)
    .setInteractive().setDepth(-1);
  container.add(blocker);
  blocker.on('pointerdown', () => {
    saveEnemyTypeStatOverrides();
    container.destroy();
    onClose();
  });

  return container;
}

// ── Slider helper ──
function addSlider(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  x: number, y: number,
  sd: SliderDef,
  value: number,
  onChange: (val: number) => void,
): number {
  // Label
  const lbl = scene.add.text(x, y, sd.label, {
    fontSize: '9px', fontFamily: 'monospace', color: '#aaaacc',
  });
  container.add(lbl);

  const trackY = y + 13;

  // Track bg
  const trackBg = scene.add.rectangle(x + TRACK_W / 2, trackY, TRACK_W, 3, 0x333355);
  container.add(trackBg);

  // Value text
  const valText = scene.add.text(x + TRACK_W + 6, trackY - 5, value.toFixed(1), {
    fontSize: '9px', fontFamily: 'monospace', color: '#ffffff',
  });
  container.add(valText);

  // Knob
  const ratio = (value - sd.min) / (sd.max - sd.min);
  const knob = scene.add.rectangle(x + ratio * TRACK_W, trackY, 7, 11, 0x1a1a2e)
    .setStrokeStyle(1.5, 0xff8844);
  container.add(knob);

  // Hit zone
  const hit = scene.add.rectangle(x + TRACK_W / 2, trackY, TRACK_W, 16, 0x000000, 0)
    .setInteractive({ cursor: 'pointer' });
  container.add(hit);

  const apply = (px: number) => {
    // Convert global pointer x to local container x
    const localX = px - container.x;
    const r = Phaser.Math.Clamp((localX - x) / TRACK_W, 0, 1);
    let raw = sd.min + r * (sd.max - sd.min);
    raw = Math.round(raw / sd.step) * sd.step;
    raw = Phaser.Math.Clamp(raw, sd.min, sd.max);
    knob.setX(x + ((raw - sd.min) / (sd.max - sd.min)) * TRACK_W);
    valText.setText(raw.toFixed(1));
    onChange(raw);
  };

  hit.on('pointerdown', (p: Phaser.Input.Pointer) => apply(p.x));
  scene.input.on('pointermove', (p: Phaser.Input.Pointer) => {
    if (p.isDown && hit.getBounds().contains(p.x, p.y)) apply(p.x);
  });

  return trackY + 18;
}
